import { FormApi } from 'final-form'
import { StudioSystemType } from 'types/global'
import { DropdownInput, IntegrationData, NativeMountingInputs } from 'types/mounting'
import { createDlubalUKSnowZoneButton, createDlubalUKWindZoneButton } from '../../dlubalButtonConfigs'
import { ComptibilityRules, FilterCriteria, filterOptions } from '../../inputFilters'
import { RELATIVE_TILT_OPTIONS } from './SolarPortTypes'

const compatibilityRules: ComptibilityRules = {
  postType: {
    configCompatibility: {
      singlePost: ['1inP'],
      twinPost: ['1inP', '2inP'],
    },
  },
  foundation: {
    postTypeCompatibility: {
      xAnchor: ['twinPost'],
      pile2m: ['singlePost', 'twinPost'],
      pile1_5m: ['singlePost', 'twinPost'],
      ballastBase: ['twinPost'],
      groundScrew: ['twinPost'],
      directFix: ['twinPost'],
    },
  },
  relativeTilt: {
    windZoneCompatibility: {
      20: ['standard', 'high'],
      25: ['standard'],
      30: ['standard'],
    },
  },
}

export function createSolarPortInputs() {
  return (_projectForm: FormApi, system: StudioSystemType): NativeMountingInputs => {
    const integrationData = (system.integration_json?.solarPortGroundMount ?? {}) as IntegrationData

    const dropdownInputs: DropdownInput[] = [
      {
        label: 'Portrait configuration',
        variableName: 'portraitConfig',
        default: '1inP',
        options: [
          { label: '1 in Portrait', value: '1inP' },
          { label: '2 in Portrait', value: '2inP' },
        ],
      },
      {
        label: 'Post type',
        variableName: 'postType',
        default: 'singlePost',
        options: [
          { label: 'Single Post', value: 'singlePost' },
          { label: 'Twin Post', value: 'twinPost' },
        ],
      },
      {
        label: 'Relative Tilt',
        variableName: 'relativeTilt',
        default: 30,
        options: RELATIVE_TILT_OPTIONS.map((p) => ({ label: `${p}°`, value: p })),
      },
      {
        label: 'Foundation type',
        variableName: 'foundation',
        default: 'pile1_5m',
        options: [
          { label: '2m Pile', value: 'pile2m' },
          { label: '1.5m Pile', value: 'pile1_5m' },
          { label: 'X-Anchor', value: 'xAnchor' },
          { label: 'Ballast Base', value: 'ballastBase' },
          { label: 'Ground Screw', value: 'groundScrew' },
          { label: 'Direct Fix', value: 'directFix' },
        ],
      },
      {
        label: 'Wind zone',
        variableName: 'windZone',
        default: 'high',
        options: [
          { label: 'Standard (up to 26 m/s)', value: 'standard' },
          { label: 'High (up to 30.3 m/s)', value: 'high' },
        ],
        button: createDlubalUKWindZoneButton(['solarPortGroundMount']),
      },
      {
        label: 'Snow zone',
        variableName: 'snowZone',
        default: 5,
        options: [
          { label: 'Zone 1 (0.3 kN/m²)', value: 1 },
          { label: 'Zone 2 (0.4 kN/m²)', value: 2 },
          { label: 'Zone 3 (0.5 kN/m²)', value: 3 },
          { label: 'Zone 4 (0.6 kN/m²)', value: 4 },
          { label: 'Zone 5 (0.7 kN/m²)', value: 5 },
          { label: 'Zone 6.5 (0.85 kN/m²)', value: 6.5 },
        ],
        button: createDlubalUKSnowZoneButton(['solarPortGroundMount']),
      },
    ]

    const filterCriteria: FilterCriteria[] = [
      { filterType: 'configCompatibility', filterValue: '#/portraitConfig' },
      { filterType: 'postTypeCompatibility', filterValue: '#/postType' },
      { filterType: 'windZoneCompatibility', filterValue: '#/windZone' },
    ]

    const filteredDropdownInputs = filterOptions(dropdownInputs, compatibilityRules, filterCriteria, integrationData)

    const portraitConfig = integrationData.portraitConfig ?? '1inP'
    const postType = integrationData.postType ?? 'singlePost'
    if (portraitConfig === '1inP' && postType === 'twinPost') {
      const foundationInput = filteredDropdownInputs.find((d) => d.variableName === 'foundation')
      if (foundationInput) {
        foundationInput.options = foundationInput.options.filter((o) => o.value !== 'pile2m' && o.value !== 'pile1_5m')
        if (!foundationInput.options.some((o) => o.value === foundationInput.default)) {
          foundationInput.default = foundationInput.options[0]?.value ?? foundationInput.default
        }
      }
    }

    return {
      dropdownInputs: filteredDropdownInputs,
      textFieldInputs: [
        {
          label: 'Ground slope (degrees)',
          variableName: 'groundSlope',
          type: 'number',
          default: 0,
          min: 0,
        },
        {
          label: 'Array spacing (mm)',
          variableName: 'arraySpacing',
          type: 'number',
          default: integrationData.portraitConfig === '2inP' ? 5000 : 4000,
          min: 0,
        },
      ],
    }
  }
}
